import Button from '@material-ui/core/Button'
import Chip from '@material-ui/core/Chip'
import Grid from '@material-ui/core/Grid'
import Modal from '@material-ui/core/Modal'
import Paper from '@material-ui/core/Paper'
import { makeStyles } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import Tooltip from '@material-ui/core/Tooltip'
import StopRoundedIcon from '@material-ui/icons/StopRounded'
import Autocomplete, {
	createFilterOptions,
} from '@material-ui/lab/Autocomplete'
import type { FilterOptionsState } from '@material-ui/lab/useAutocomplete/useAutocomplete'
import type React from 'react'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import LoadingButton from 'src/components/ui/Button/LoadingButton'
import {
	setAddTagsModalOpenStatus,
	updatePurchaseTags,
} from 'src/redux/slices/purchases'
import type { ReduxPurchase } from 'src/types/apiType'
import {
	useAppDispatch,
	useAppSelector,
	useAppThunkDispatch,
} from 'src/utils/hooks/reduxHooks'

const colorsRow1 = [
	'#ff4d4d',
	'#70db70',
	'#4d4dff',
	'#ff4dd2',
	'#db4dff',
	'#ccccff',
	'#669999',
	'#4dffff',
]
const colorsRow2 = [
	'#ff9933',
	'#ffff33',
	'#ccff33',
	'#ad33ff',
	'#e6e6ff',
	'#7070db',
	'#ff33ff',
	'#ffe6ff',
]

const useStyles = makeStyles((theme) => ({
	saveModal: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	saveModalContent: {
		borderRadius: '10px',
		backgroundColor: theme.palette.background.paper,
		maxWidth: '50%',
	},
	formContainer: {
		padding: theme.spacing(3),
	},
	buttonsContainer: {
		flexGrow: 1,
		marginLeft: 'auto',
	},
	button: {
		padding: theme.spacing(1),
	},
	errorMsg: {
		color: 'red',
		padding: 0,
		'&:before': {
			display: 'inline',
			content: '"⚠"',
		},
	},
	paper: {
		border: '1px solid',
		padding: theme.spacing(1),
		backgroundColor: 'black',
	},
}))

function AddPurchaseTagsModal({ row }: { row: ReduxPurchase }) {
	const classes = useStyles()
	const { t } = useTranslation()
	const { t: tPurchases } = useTranslation('purchases')
	const { t: tClientFormModal } = useTranslation('ClientFormModal')
	const { handleSubmit, errors, control, watch } = useForm({
		mode: 'all',
	})

	const dispatch = useAppDispatch()
	const dispatchThunk = useAppThunkDispatch()
	const addTagsModalOpenStatus = useAppSelector(
		(state) => state.purchases.addTagsModalOpenStatus
	)
	const updatePurchaseApiStatus = useAppSelector(
		(state) => state.purchases.updatePurchaseApiStatus
	)
	const companyInfo = useAppSelector((state) => state.settings.companyInfo)
	const companyTags = companyInfo?.purchaseTags || []
	const companyTagsTitleArray = companyTags.map((t) => t?.title)
	const [open, setOpen] = useState(false)
	const [chipColor, setChipColor] = useState('#a6a6a6')
	const [tagsData, setTagsData] = useState<
		[] | { title: string; color: string }[]
	>([])
	const filter = createFilterOptions<string | null | undefined>()
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
	const noOfTags = watch('color')
	const openPop = Boolean(anchorEl)
	const id = openPop ? 'simple-popper' : undefined

	const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
		const newTarget = anchorEl ? null : event.currentTarget
		setAnchorEl(newTarget)
	}

	const handleClose = () => {
		setOpen(false)
	}

	const handleOpen = () => {
		setOpen(true)
	}
	const handleColor = () => {
		handleClose()
		setChipColor('#a6a6a6')
	}
	const setColor = (c: string, option: string | null | undefined) => {
		if (!option) {
			console.error('Tag title is unavailable')
			return
		}
		setChipColor(c)
		setTagsData([...tagsData, { title: option, color: c }])
	}

	const handleCloseModal = () => {
		dispatch(setAddTagsModalOpenStatus({ status: false }))
	}
	const submitTags = (data: { color: string[] }) => {
		if (!row.id) {
			return console.error('row.id does not exist')
		}
		const newCompanyTagsArray = [] as { title: string; color: string }[]
		// map over all tags(array of strings)
		data.color.map((d) => {
			if (companyTagsTitleArray.includes(d)) return d
			if (tagsData.length > 0) {
				// Check if tag doesn't exists in tagsdata state, which means new tags was created but not assigned color, and push in new array
				if (!tagsData.some((t) => t.title === d)) {
					newCompanyTagsArray.push({ title: d, color: chipColor })
				}
				// Check if tag exists in tagsdata state, which means new tags was created and assigned color, and push in new array
				else if (tagsData.some((t) => t.title === d)) {
					newCompanyTagsArray.push(
						tagsData.filter((t) => t.title === d)[0]
					)
				}
			} else {
				if (!companyTagsTitleArray.includes(d)) {
					newCompanyTagsArray.push({ title: d, color: chipColor })
				}
			}
		})

		//  data is the array to be updated in purchase
		// Push newCompanyTagsArray into the tags array in company and update
		dispatchThunk(
			updatePurchaseTags({
				purchaseId: row.id,
				tagNames: data.color,
				newCompanyTagsArray: newCompanyTagsArray,
			})
		)
	}

	function getFilterOptions(
		options: Array<string | null | undefined>,
		params: FilterOptionsState<string | null | undefined>
	) {
		if (!params || options.some((o) => !o)) {
			console.error('params or Options is null | undefined ')
			return []
		}
		const filtered = filter(options, params)

		// Suggest the creation of a new value
		if (params.inputValue !== '') {
			filtered.push(params?.inputValue)
		}
		const results = filtered.filter(
			(p, idx, self) => idx === self.findIndex((t) => t === p)
		)
		return results
	}
	function getOptionLabel(option: string | null | undefined) {
		// Value selected with enter, right from the input
		if (companyTags?.some((t) => t?.title === option) && option) {
			return option
		}

		// Regular option
		return `Create ${option}`
	}
	return (
		<>
			<Modal
				className={classes.saveModal}
				open={addTagsModalOpenStatus}
				onClose={(event, reason) => {
					if (
						reason !== 'backdropClick' &&
						reason !== 'escapeKeyDown'
					) {
						handleCloseModal()
					}
				}}
				aria-labelledby="add-purchase-tags-dialog"
			>
				<div className={classes.saveModalContent}>
					<Grid container>
						<Paper className={classes.formContainer}>
							<form
								onSubmit={handleSubmit(submitTags)}
								noValidate
							>
								<Grid item container spacing={4}>
									<Grid item xs={12}>
										{tPurchases('addTagsModal.title')}
									</Grid>
									<Grid item xs={12}>
										<Controller
											control={control}
											name="color"
											fullWidth
											size="large"
											defaultValue=""
											rules={{ required: true }}
											errors={errors.color}
											render={(props) => {
												return (
													<Autocomplete
														multiple
														id="tags-filled"
														freeSolo
														defaultValue={
															row?.tags?.map(
																(tags) => tags
															) || []
														}
														options={
															companyTags?.map(
																(option) =>
																	option?.title
															) || []
														}
														renderTags={(
															value,
															getTagProps
														) =>
															value.map(
																(
																	option,
																	index
																) => {
																	return (
																		<Tooltip
																			interactive
																			arrow
																			disableHoverListener
																			title={
																				<div
																					style={{
																						display:
																							'flex',
																						flexDirection:
																							'column',
																					}}
																				>
																					<div>
																						{colorsRow1.map(
																							(
																								c
																							) => (
																								<StopRoundedIcon
																									style={{
																										color: c,
																										cursor: 'pointer',
																									}}
																									onClick={() =>
																										setColor(
																											c,
																											option
																										)
																									}
																								/>
																							)
																						)}
																					</div>
																					<div>
																						{colorsRow2.map(
																							(
																								c
																							) => (
																								<StopRoundedIcon
																									style={{
																										color: c,
																										cursor: 'pointer',
																									}}
																									onClick={() =>
																										setColor(
																											c,
																											option
																										)
																									}
																								/>
																							)
																						)}
																					</div>
																				</div>
																			}
																			open={
																				open &&
																				index ===
																					value.length -
																						1 &&
																				!companyTags?.some(
																					(
																						t
																					) =>
																						t?.title ===
																						option
																				)
																			}
																			onClose={
																				handleClose
																			}
																			onOpen={
																				handleOpen
																			}
																		>
																			<Chip
																				label={
																					option
																				}
																				onClick={
																					handleClick
																				}
																				id={
																					id
																				}
																				onDelete={
																					handleColor
																				}
																				style={{
																					backgroundColor:
																						companyTags?.filter(
																							(
																								t
																							) =>
																								t?.title ===
																								option
																						)[0]
																							?.color ||
																						tagsData.filter(
																							(
																								t
																							) =>
																								t?.title ===
																								option
																						)[0]
																							?.color ||
																						chipColor,
																				}}
																				{...getTagProps(
																					{
																						index,
																					}
																				)}
																			/>
																		</Tooltip>
																	)
																}
															)
														}
														filterOptions={(
															options,
															params
														) =>
															getFilterOptions(
																options,
																params
															)
														}
														getOptionLabel={(
															option
														) =>
															getOptionLabel(
																option
															)
														}
														renderOption={(
															option,
															state
														) => {
															if (
																companyTags?.some(
																	(t) =>
																		t?.title ===
																		option
																)
															) {
																const color =
																	companyTags?.filter(
																		(t) =>
																			t?.title ===
																			option
																	)[0]?.color
																if (!color) {
																	console.error(
																		"Color doesn't exist in companyTags"
																	)
																	return
																}
																return (
																	<Chip
																		label={
																			option
																		}
																		style={{
																			backgroundColor:
																				color,
																			cursor: 'pointer',
																		}}
																	/>
																)
															}
															return `Create ${option}`
														}}
														renderInput={(
															params
														) => (
															<TextField
																{...params}
																label={tPurchases(
																	'addTags'
																)}
																onChange={
																	handleColor
																}
																onClick={
																	handleColor
																}
																variant="outlined"
																error={
																	noOfTags?.length >
																	3
																}
																helperText={
																	noOfTags?.length >
																	3
																		? tPurchases(
																				'addTagsModal.maxTags'
																			)
																		: ''
																}
															/>
														)}
														onChange={(_, data) => {
															handleOpen()
															return props.onChange(
																data
															)
														}}
														onBlur={handleColor}
														onKeyDown={(event) => {
															if (
																event.key ===
																'Backspace'
															) {
																handleColor()
															}
														}}
													/>
												)
											}}
										/>
									</Grid>

									<Grid
										item
										xs={12}
										className={classes.buttonsContainer}
									>
										<span className={classes.button}>
											<LoadingButton
												type="submit"
												variant="contained"
												color="primary"
												disabled={noOfTags?.length > 3}
												loading={
													updatePurchaseApiStatus ===
													'loading'
														? true
														: false
												}
												className={classes.button}
											>
												{tClientFormModal('save')}
											</LoadingButton>
										</span>
										<span className={classes.button}>
											<Button
												variant="contained"
												color="secondary"
												className={classes.button}
												onClick={handleCloseModal}
											>
												{tClientFormModal('cancel')}
											</Button>
										</span>
									</Grid>
								</Grid>
							</form>
						</Paper>
					</Grid>
				</div>
			</Modal>
		</>
	)
}

export default AddPurchaseTagsModal
